import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  IconKey,
  IconMail,
  IconMailForward,
  IconClock,
  IconShieldLock,
  IconLoader2,
  IconAlertCircle,
  IconCircleCheck,
} from "@tabler/icons-react";
import classes from "./resetPasswordRedesign.module.css";
import { talentResetPass } from "../../../services/talentCalls";
import { clientResetPass } from "../../../services/clientCalls";
import { errorHandler } from "../../../utils";
import { Logo } from "../../../constants";

const BrandLogo = () => (
  <a className={classes.brandLogo} href="https://www.awasource.com">
    <Logo color="#ffffff" />
  </a>
);

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ResetPasswordRedesign = () => {
  const navigate = useNavigate();
  const [param] = useSearchParams();
  const status = param.get("status") === "client" ? "client" : param.get("status") === "talent" ? "talent" : null;

  useEffect(() => {
    if (!status) navigate("/login");
  }, [status, navigate]);

  /* Lock page scroll on desktop, where this is a fixed one-screen split layout */
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 981px)");
    const applyLock = () => {
      const locked = mql.matches ? "hidden" : "";
      document.documentElement.style.overflow = locked;
      document.body.style.overflow = locked;
    };
    applyLock();
    mql.addEventListener("change", applyLock);
    return () => {
      mql.removeEventListener("change", applyLock);
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState(false);
  const [shake, setShake] = useState(false);
  const [load, setLoad] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!emailRe.test(email.trim())) {
      setEmailErr(true);
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }

    try {
      setLoad(true);
      if (status === "talent") {
        localStorage.setItem("role", "talent");
        await talentResetPass({ email });
      } else {
        localStorage.setItem("role", "client");
        await clientResetPass({ email });
      }
      setSent(true);
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    } finally {
      setLoad(false);
    }
  };

  if (!status) return null;

  return (
    <div className={classes.authShell}>
      <div className={classes.pageBg}>
        <div className={`${classes.brandBlob} ${classes.b1}`} />
        <div className={`${classes.brandBlob} ${classes.b2}`} />
        <div className={`${classes.brandBlob} ${classes.b3}`} />
      </div>

      <aside className={classes.brandPanel}>
        <div className={classes.brandTop}>
          <BrandLogo />
          <span className={classes.brandPanelTag}>Africa's hiring layer</span>
        </div>

        <div className={classes.brandMiddle}>
          <h1 className={classes.brandHeadline}>
            Forgot
            <br />
            something?
          </h1>
          <p className={classes.brandSub}>
            It happens to the best of us. Confirm your email and we'll get you a secure link back into your account in
            seconds.
          </p>

          <div className={classes.brandVisual}>
            <div className={`${classes.floatCard} ${classes.fcA}`}>
              <span className={classes.fcIcon} style={{ background: "#eff2ff", color: "#1d4ed8" }}>
                <IconMailForward size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>Link sent instantly</div>
                <div className={classes.fcSub}>Straight to your inbox</div>
              </div>
            </div>
            <div className={`${classes.floatCard} ${classes.fcB}`}>
              <span className={classes.fcIcon} style={{ background: "#fef3c7", color: "#d97706" }}>
                <IconClock size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>Expires in 15 min</div>
                <div className={classes.fcSub}>For your security</div>
              </div>
            </div>
            <div className={`${classes.floatCard} ${classes.fcC}`}>
              <span className={classes.fcIcon} style={{ background: "#ecfdf5", color: "#059669" }}>
                <IconShieldLock size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>Encrypted end to end</div>
                <div className={classes.fcSub}>Only you can use it</div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.brandBottom}>
          <div className={classes.brandBottomAvatars}>
            <span>AO</span>
            <span>KM</span>
            <span>NB</span>
          </div>
          <p className={classes.brandBottomText}>
            <strong>10k+</strong> verified profiles
          </p>
        </div>
      </aside>

      <div className={classes.mobileHeaderBar}>
        <div className={classes.mobileHeaderBarInner}>
          <BrandLogo />
        </div>
      </div>

      <main className={classes.formPanel}>
        <div className={classes.formCard}>
          <div className={classes.formPanelInner}>
            <div className={`${classes.formHeader} ${classes.animIn}`}>
              <span className={classes.formEyebrow}>
                <IconKey size={13} /> Account recovery
              </span>
              <h1 className={classes.formTitle}>Reset Password</h1>
              <p className={classes.formSubtitle}>
                Enter the email address associated with your Awasource account and we'll send you a reset link.
              </p>
            </div>

            <form className={classes.animIn} onSubmit={submit} noValidate>
              <div className={classes.field}>
                <label className={classes.fieldLabel} htmlFor="resetEmail">
                  Email Address
                </label>
                <div className={classes.inputWrap}>
                  <IconMail size={16} className={classes.inputIcon} />
                  <input
                    id="resetEmail"
                    className={`${classes.formInput} ${emailErr ? classes.formInputErr : ""} ${shake ? classes.shake : ""}`}
                    type="email"
                    placeholder="janedoe@awasource.com"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailErr(false);
                    }}
                  />
                </div>
                <div className={`${classes.fieldError} ${emailErr ? classes.fieldErrorOn : ""}`}>
                  <IconAlertCircle size={13} /> Enter a valid email address.
                </div>
              </div>

              <button type="submit" className={classes.btnPrimary} disabled={load}>
                {load ? (
                  <>
                    <IconLoader2 size={16} className={classes.spinIcon} /> Sending…
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </button>

              {sent && (
                <div className={`${classes.successBanner} ${classes.on}`}>
                  <IconCircleCheck size={18} />
                  <span>Reset link sent — check your inbox.</span>
                </div>
              )}
            </form>

            <p className={classes.backLinkRow}>
              <Link className={classes.backLink} to="/login">
                Click here to go back
              </Link>
            </p>

            <p className={classes.formFooter}>
              By proceeding you have agreed to our <Link to="/terms-of-service">Terms of Use</Link> &amp;{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordRedesign;
